import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Eye, EyeOff, ImagePlus, Loader2, LogOut, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, discountOf, errMsg, fileUrl, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const slugify = (t) => t.toLowerCase().normalize("NFD").replace(/[̀-ͯ\s]/g, "-");

const EMPTY = { name: "", brand: "", category: "maquillage", subcategory: "", description: "", details: "", price: "", reference_price: "", stock: 0, is_new: false, coming_soon: false, hidden: false, variants: [] };
const NOTIFY_LABELS = {
  new_order: "Nouvelle commande",
  payment_received: "Paiement reçu",
  new_idea: "Nouvelle suggestion",
  new_waitlist: "Nouvelle alerte produit (Me prévenir)",
  new_customer: "Nouveau compte cliente",
  low_stock: "Produit presque épuisé",
  out_of_stock: "Produit épuisé",
  contact_request: "Demande de contact",
};
const ORDER_STATUS = {
  en_attente_paiement: "En attente de paiement",
  paiement_recu: "Paiement reçu",
  confirmee: "Commande confirmée",
  en_preparation: "En préparation",
  expediee: "Expédiée",
  livree: "Livrée",
  annulee: "Annulée",
};
const IDEA_STATUS = { a_etudier: "À étudier", en_preparation: "En préparation", ajoutee: "Ajoutée à la boutique", non_retenue: "Non retenue" };

function Login() {
  const { login, user, logout } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const u = await login(form.email, form.password);
      if (u.role !== "admin") toast.error("Ce compte n'est pas administrateur");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="max-w-sm mx-auto pt-24 px-5" data-testid="admin-login">
      <form onSubmit={submit} className="rounded-3xl border border-kay-line bg-white p-8 grid gap-4">
        <h1 className="font-display text-3xl text-center">Espace <span className="italic text-kay-pink">admin</span></h1>
        <p className="text-center text-xs text-kay-ink/40 -mt-2">Réservé à la propriétaire de la boutique.</p>
        {user && user.role !== "admin" && (
          <div className="rounded-xl bg-kay-rose/40 border border-kay-line px-4 py-3 text-xs text-center grid gap-2">
            <span>Connectée en tant que {user.email} (compte cliente — accès refusé).</span>
            <button type="button" onClick={logout} className="font-bold underline" data-testid="admin-switch-account">Se déconnecter pour changer de compte</button>
          </div>
        )}
        <input required type="email" data-testid="admin-email-input" placeholder="Email admin" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink" />
        <input required type="password" data-testid="admin-password-input" placeholder="Mot de passe" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink" />
        <button data-testid="admin-login-button" className="rounded-full bg-kay-ink text-white py-3 text-sm font-bold hover:bg-kay-pink transition-colors">Entrer</button>
      </form>
    </div>
  );
}

function VariantImage({ pid, idx, image, onDone }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/products/${pid}/variants/${idx}/image`, fd);
      onDone(data.path);
      toast.success("Photo du coloris ajoutée");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <>
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="w-10 h-10 rounded-lg border border-dashed border-kay-rosedeep overflow-hidden flex items-center justify-center text-kay-pink shrink-0 hover:bg-kay-rose/40" data-testid={`variant-image-${idx}`} aria-label="Photo du coloris">
        {busy ? <Loader2 size={14} className="animate-spin" /> : image ? <img src={fileUrl(image)} alt="" className="w-full h-full object-cover" /> : <ImagePlus size={14} />}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} />
    </>
  );
}

function ProductForm({ initial, cats, subs = [], onSaved, onCancel }) {
  const [f, setF] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState([]);
  const imgRef = useRef(null);
  const num = (v) => (v === "" || v == null ? null : parseFloat(v));
  const upV = (i, v) => setF({ ...f, variants: f.variants.map((x, j) => (j === i ? v : x)) });
  const rmV = (i) => setF({ ...f, variants: f.variants.filter((_, j) => j !== i) });
  const addV = () => setF({ ...f, variants: [...f.variants, { name: "", stock: 5, image: null, ref: "", hidden: false }] });

  const pickImages = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setPending((prev) => [...prev, ...files.map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...f,
      price: num(f.price),
      reference_price: num(f.reference_price),
      stock: parseInt(f.stock) || 0,
      variants: f.variants.filter((v) => v.name.trim()).map((v) => ({ name: v.name.trim(), stock: parseInt(v.stock) || 0, image: v.image || null, ref: (v.ref || "").trim() || null, hidden: !!v.hidden })),
    };
    try {
      let pid = f.id;
      if (pid) await api.put(`/admin/products/${pid}`, payload);
      else {
        const { data } = await api.post("/admin/products", payload);
        pid = data.id;
      }
      for (const p of pending) {
        const fd = new FormData();
        fd.append("file", p.file);
        try {
          await api.post(`/admin/products/${pid}/images`, fd);
        } catch {
          toast.error(`Photo « ${p.file.name} » non envoyée`);
        }
      }
      toast.success("Produit enregistré");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };
  const inp = "rounded-xl border border-kay-line px-3.5 py-2.5 text-sm outline-none focus:border-kay-pink w-full";
  return (
    <form onSubmit={submit} className="rounded-2xl border border-kay-rosedeep bg-kay-rose/20 p-5 grid gap-3" data-testid="product-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <input required placeholder="Nom du produit *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} data-testid="pf-name" />
        <input placeholder="Marque" value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} className={inp} data-testid="pf-brand" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inp} data-testid="pf-category">
          {cats.map((c) => (
            <option key={c.slug} value={c.slug}>{c.label}</option>
          ))}
        </select>
        <select value={f.subcategory || ""} onChange={(e) => setF({ ...f, subcategory: e.target.value })} className={inp} data-testid="pf-subcategory">
          <option value="">— Aucune sous-catégorie —</option>
          {subs.filter((s) => !s.hidden && s.category === f.category).map((s) => (
            <option key={s.slug} value={s.slug}>{s.label}</option>
          ))}
        </select>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <input type="number" step="0.01" placeholder="Notre prix (€)" value={f.price ?? ""} onChange={(e) => setF({ ...f, price: e.target.value })} className={inp} data-testid="pf-price" />
        <input type="number" step="0.01" placeholder="Prix initial réel (€)" value={f.reference_price ?? ""} onChange={(e) => setF({ ...f, reference_price: e.target.value })} className={inp} data-testid="pf-ref-price" />
        <input type="number" placeholder="Stock (si sans coloris)" value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} className={inp} data-testid="pf-stock" />
      </div>
      <div className="flex gap-6 items-center flex-wrap">
        <label className="flex items-center gap-2 text-xs font-semibold"><input type="checkbox" checked={f.is_new} onChange={(e) => setF({ ...f, is_new: e.target.checked })} className="accent-[#E05297]" data-testid="pf-new" /> Nouveauté</label>
        <label className="flex items-center gap-2 text-xs font-semibold"><input type="checkbox" checked={f.coming_soon} onChange={(e) => setF({ ...f, coming_soon: e.target.checked })} className="accent-[#E05297]" data-testid="pf-coming" /> Coming soon</label>
        <label className="flex items-center gap-2 text-xs font-semibold"><input type="checkbox" checked={!!f.hidden} onChange={(e) => setF({ ...f, hidden: e.target.checked })} className="accent-[#E05297]" data-testid="pf-hidden" /> Masquer le produit</label>
      </div>
      <textarea placeholder="Description" rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={`${inp} resize-none`} data-testid="pf-description" />
      <textarea placeholder="Détails du produit (contenance, ingrédients, conseils d'application… affichés dans l'onglet « Détails » de la fiche)" rows={2} value={f.details || ""} onChange={(e) => setF({ ...f, details: e.target.value })} className={`${inp} resize-none`} data-testid="pf-details" />

      <div className="grid gap-2 border-t border-kay-rosedeep/50 pt-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/50">Photos du produit</p>
        <p className="text-[11px] text-kay-ink/40 -mt-1">La première photo est l'image générale, affichée partout. Ajoute des photos secondaires pour la galerie de la fiche produit.</p>
        <div className="flex gap-2 flex-wrap items-center">
          {(f.images || []).map((img) => (
            <div key={img} className="w-14 h-14 rounded-lg overflow-hidden border border-kay-line" data-testid={`pf-image-existing-${img}`}>
              <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
          {pending.map((p, i) => (
            <div key={p.preview} className="relative w-14 h-14 rounded-lg overflow-hidden border-2 border-kay-pink group" data-testid={`pf-image-pending-${i}`}>
              <img src={p.preview} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => setPending(pending.filter((_, j) => j !== i))} className="absolute inset-0 bg-kay-ink/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Retirer la photo" data-testid={`pf-image-remove-${i}`}>
                <X size={14} />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => imgRef.current?.click()} className="w-14 h-14 rounded-lg border border-dashed border-kay-rosedeep flex items-center justify-center text-kay-pink hover:bg-kay-rose/40 transition-colors" data-testid="pf-image-add" aria-label="Ajouter une photo">
            <ImagePlus size={16} />
          </button>
          <input ref={imgRef} type="file" accept="image/*" multiple className="hidden" onChange={pickImages} data-testid="pf-image-input" />
        </div>
        {pending.length > 0 && (
          <p className="text-[11px] text-kay-pink font-semibold" data-testid="pf-image-pending-note">{pending.length} nouvelle(s) photo(s) — envoyée(s) en cliquant sur « Enregistrer ».</p>
        )}
      </div>

      <div className="grid gap-2 border-t border-kay-rosedeep/50 pt-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/50">Coloris / Teintes disponibles</p>
        <p className="text-[11px] text-kay-ink/40 -mt-1">Nom · stock · référence (facultative) · photo · visibilité. Ajoute autant de teintes que tu veux, même plus tard en rouvrant le produit.</p>
        {f.variants.map((v, i) => (
          <div key={i} className={`flex gap-2 items-center flex-wrap sm:flex-nowrap rounded-xl transition-opacity ${v.hidden ? "opacity-50" : ""}`} data-testid={`variant-row-${i}`}>
            <input placeholder="Nom du coloris" value={v.name} onChange={(e) => upV(i, { ...v, name: e.target.value })} className={inp} data-testid={`variant-name-${i}`} />
            <input type="number" value={v.stock} onChange={(e) => upV(i, { ...v, stock: e.target.value })} className={`${inp} !w-20 shrink-0`} placeholder="Stock" data-testid={`variant-stock-${i}`} />
            <input placeholder="Réf. (facult.)" value={v.ref || ""} onChange={(e) => upV(i, { ...v, ref: e.target.value })} className={`${inp} !w-28 shrink-0`} data-testid={`variant-ref-${i}`} />
            {f.id && <VariantImage pid={f.id} idx={i} image={v.image} onDone={(path) => upV(i, { ...v, image: path })} />}
            <button
              type="button"
              onClick={() => upV(i, { ...v, hidden: !v.hidden })}
              title={v.hidden ? "Coloris masqué — cliquer pour réafficher" : "Masquer temporairement ce coloris"}
              className={`p-2 shrink-0 transition-colors ${v.hidden ? "text-kay-pink" : "text-kay-ink/40 hover:text-kay-ink"}`}
              aria-label="Masquer ou afficher le coloris"
              data-testid={`variant-hide-${i}`}
            >
              {v.hidden ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
            <button type="button" onClick={() => rmV(i)} className="p-2 text-red-400 hover:text-red-600 shrink-0" aria-label="Supprimer le coloris" data-testid={`variant-delete-${i}`}>
              <X size={14} />
            </button>
          </div>
        ))}
        {f.variants.some((v) => v.hidden) && (
          <p className="text-[11px] text-kay-pink italic">Les coloris barrés d'une pastille rose sont masqués : invisibles sur la boutique, mais conservés ici.</p>
        )}
        <button type="button" onClick={addV} className="justify-self-start rounded-full border border-dashed border-kay-rosedeep px-4 py-1.5 text-xs font-bold text-kay-pink hover:bg-kay-rose/40 transition-colors" data-testid="variant-add">
          + Ajouter un coloris
        </button>
        {!f.id && f.variants.length > 0 && (
          <p className="text-[11px] text-kay-ink/40">Enregistre d'abord le produit, puis rouvre-le pour ajouter les photos des coloris.</p>
        )}
      </div>

      {f.reference_price > 0 && f.price > 0 && f.reference_price > f.price && (
        <p className="font-mono text-xs text-kay-green font-bold">Badge affiché : -{Math.round((1 - f.price / f.reference_price) * 100)} % · économie {fmt(f.reference_price - f.price)}</p>
      )}
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="rounded-full bg-kay-ink text-white px-6 py-2.5 text-xs font-bold hover:bg-kay-pink transition-colors disabled:opacity-50" data-testid="pf-save">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-full border border-kay-line px-6 py-2.5 text-xs font-bold" data-testid="pf-cancel">Annuler</button>
      </div>
    </form>
  );
}

function ImageManager({ product, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const ref = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post(`/admin/products/${product.id}/images`, fd);
      toast.success("Photo ajoutée");
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const del = async (path) => {
    await api.delete(`/admin/products/${product.id}/images`, { params: { path } });
    onChanged();
  };
  const move = async (from, to) => {
    setDragIdx(null);
    setOverIdx(null);
    if (from === to || from == null || to == null) return;
    const arr = [...(product.images || [])];
    const [it] = arr.splice(from, 1);
    arr.splice(to, 0, it);
    try {
      await api.put(`/admin/products/${product.id}/images`, { images: arr });
      if (from === 0 || to === 0) toast.success("Nouvelle image principale définie");
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="mt-2" data-testid={`image-manager-${product.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {product.images?.map((p, i) => (
          <div
            key={p}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
            onDragLeave={() => setOverIdx(null)}
            onDrop={() => move(dragIdx, i)}
            onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
            data-testid={`image-thumb-${product.id}-${i}`}
            className={`relative w-14 h-14 rounded-lg overflow-hidden border group cursor-grab active:cursor-grabbing transition-all ${
              overIdx === i && dragIdx !== i ? "border-kay-pink scale-110" : i === 0 ? "border-kay-pink" : "border-kay-line"
            } ${dragIdx === i ? "opacity-40" : ""}`}
            title={i === 0 ? "Image principale — glisse pour réordonner" : "Glisse pour réordonner"}
          >
            <img src={fileUrl(p)} alt="" className="w-full h-full object-cover pointer-events-none" />
            {i === 0 && (
              <span className="absolute bottom-0 inset-x-0 bg-kay-pink text-white font-mono text-[8px] font-bold text-center py-0.5" data-testid={`image-main-badge-${product.id}`}>
                PRINCIPALE
              </span>
            )}
            <button onClick={() => del(p)} className="absolute inset-0 bg-kay-ink/60 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity" aria-label="Supprimer" data-testid={`image-delete-${product.id}-${i}`}>
              <X size={14} />
            </button>
          </div>
        ))}
        <button onClick={() => ref.current?.click()} disabled={uploading} className="w-14 h-14 rounded-lg border border-dashed border-kay-rosedeep flex items-center justify-center text-kay-pink hover:bg-kay-rose/40 transition-colors" data-testid={`image-add-${product.id}`} aria-label="Ajouter une photo">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
        </button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} />
      </div>
      {(product.images?.length || 0) > 1 && (
        <p className="text-[10px] text-kay-ink/40 mt-1.5">Glisse les photos pour les réordonner — la première est l'image principale.</p>
      )}
    </div>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([{ slug: "maquillage", label: "Maquillage" }, { slug: "parfums", label: "Parfums" }, { slug: "sacs", label: "Sacs" }]);
  const [subs, setSubs] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => {
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => { setCats(r.data.categories || []); setSubs(r.data.subcategories || []); }).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);
  const del = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » ?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    load();
  };
  const stockInfo = (p) =>
    p.variants?.length
      ? `${p.variants.length} coloris · stock total ${p.variants.reduce((s, v) => s + (v.stock || 0), 0)}`
      : `stock ${p.stock}`;
  return (
    <div className="grid gap-4" data-testid="admin-products-tab">
      <button onClick={() => setEditing({ ...EMPTY })} className="justify-self-start rounded-full bg-kay-ink text-white px-6 py-2.5 text-xs font-bold flex items-center gap-2 hover:bg-kay-pink transition-colors" data-testid="product-new-button">
        <Plus size={14} /> Nouveau produit
      </button>
      {editing && !editing.id && <ProductForm initial={editing} cats={cats} subs={subs} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
      {products.map((p) => (
        <div key={p.id} className={`rounded-2xl border bg-white p-5 ${p.hidden ? "border-dashed border-kay-ink/30 opacity-70" : "border-kay-line"}`} data-testid={`admin-product-${p.id}`}>
          {editing?.id === p.id ? (
            <ProductForm initial={{ ...p, price: p.price ?? "", reference_price: p.reference_price ?? "", variants: p.variants || [] }} cats={cats} subs={subs} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
          ) : (
            <div className="flex flex-wrap items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-kay-rose/40 border border-kay-line overflow-hidden shrink-0 flex items-center justify-center">
                {p.images?.[0] ? <img src={fileUrl(p.images[0])} alt="" className="w-full h-full object-cover" /> : <ImagePlus size={16} className="text-kay-pink/50" />}
              </div>
              <div className="flex-1 min-w-[160px]">
                <p className="font-mono text-[10px] uppercase tracking-widest text-kay-pink">
                  {p.brand} · {p.category}{p.subcategory ? `/${p.subcategory}` : ""}{p.coming_soon ? " · coming soon" : ""}{p.hidden ? " · MASQUÉ" : ""}
                </p>
                <p className="font-display text-xl leading-tight">{p.name}</p>
                <p className="text-xs text-kay-ink/50 mt-0.5">
                  {p.price != null ? fmt(p.price) : "Prix à définir"}
                  {p.reference_price > p.price && <> · <span className="line-through">{fmt(p.reference_price)}</span> · <span className="text-kay-pink font-bold">-{discountOf(p)} %</span></>}
                  {" · "}{stockInfo(p)}
                  {p.waitlist > 0 && <> · <span className="text-kay-green font-bold">{p.waitlist} en attente</span></>}
                </p>
                {p.variants?.length > 0 && (
                  <p className="text-[11px] text-kay-ink/40 mt-0.5" data-testid={`variant-summary-${p.id}`}>
                    {p.variants.map((v) => `${v.name} (${v.stock})${v.ref ? ` [${v.ref}]` : ""}${v.hidden ? " · masqué" : ""}`).join(" · ")}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(p)} className="rounded-full border border-kay-line px-4 py-2 text-xs font-bold flex items-center gap-1.5 hover:border-kay-ink" data-testid={`product-edit-${p.id}`}>
                  <Pencil size={12} /> Modifier
                </button>
                <button onClick={() => del(p)} className="rounded-full border border-kay-line px-4 py-2 text-xs font-bold text-red-500 hover:border-red-400" data-testid={`product-delete-${p.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          )}
          <ImageManager product={p} onChanged={load} />
        </div>
      ))}
    </div>
  );
}

function TrackingInput({ order, onSaved }) {
  const [v, setV] = useState(order.tracking || "");
  return (
    <div className="flex gap-2 items-center">
      <input
        value={v}
        onChange={(e) => setV(e.target.value)}
        placeholder="N° de suivi colis"
        className="rounded-full border border-kay-line px-4 py-1.5 text-xs outline-none focus:border-kay-pink w-44"
        data-testid={`tracking-input-${order.id}`}
      />
      <button
        onClick={async () => { await api.patch(`/admin/orders/${order.id}`, { tracking: v }); toast.success("Suivi enregistré"); onSaved(); }}
        className="rounded-full border border-kay-line px-3 py-1.5 text-[11px] font-bold hover:border-kay-ink"
        data-testid={`tracking-save-${order.id}`}
      >
        OK
      </button>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [showPendingPaypal, setShowPendingPaypal] = useState(false);
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);
  const patch = async (o, body, msg) => {
    await api.patch(`/admin/orders/${o.id}`, body);
    if (msg) toast.success(msg);
    load();
  };
  // Les commandes PayPal n'apparaissent comme commandes qu'après paiement confirmé
  const pendingPaypal = orders.filter((o) => o.payment_method === "paypal" && !o.paid && o.status !== "annulee");
  const visible = showPendingPaypal ? orders : orders.filter((o) => !pendingPaypal.includes(o));
  return (
    <div className="grid gap-4" data-testid="admin-orders-tab">
      {pendingPaypal.length > 0 && (
        <button
          type="button"
          onClick={() => setShowPendingPaypal(!showPendingPaypal)}
          data-testid="orders-pending-paypal-toggle"
          className="justify-self-start rounded-full border border-dashed border-kay-rosedeep px-4 py-1.5 text-xs font-bold text-kay-pink hover:bg-kay-rose/40 transition-colors"
        >
          {showPendingPaypal ? "Masquer" : "Voir"} {pendingPaypal.length} paiement{pendingPaypal.length > 1 ? "s" : ""} PayPal en attente (non payé{pendingPaypal.length > 1 ? "s" : ""})
        </button>
      )}
      {visible.length === 0 && <p className="font-display italic text-2xl text-kay-ink/40 text-center py-10">Aucune commande pour le moment.</p>}
      {visible.map((o) => (
        <div key={o.id} className={`rounded-2xl border bg-white p-5 grid gap-3 ${o.payment_method === "paypal" && !o.paid ? "border-dashed border-kay-rosedeep/60 opacity-70" : "border-kay-line"}`} data-testid={`admin-order-${o.id}`}>
          <div className="flex flex-wrap justify-between gap-3 items-center">
            <div>
              <p className="font-mono text-xs font-bold">{o.number} · {new Date(o.created_at).toLocaleDateString("fr-FR")}</p>
              <p className="text-sm mt-0.5">{o.customer_name} · {o.email}</p>
              <p className="text-xs text-kay-ink/50">{o.address}, {o.zip} {o.city} {o.phone && `· ${o.phone}`}</p>
              {o.payment_method === "paypal" && !o.paid && (
                <p className="font-mono text-[10px] font-bold text-kay-pink mt-1" data-testid={`order-paypal-pending-${o.id}`}>PAYPAL EN ATTENTE — PAS ENCORE PAYÉE</p>
              )}
            </div>
            <div className="text-right">
              <p className="font-display text-2xl font-semibold">{fmt(o.total)}</p>
              <p className="font-mono text-[10px] uppercase text-kay-ink/50">{o.payment_method === "paypal" ? "PayPal" : o.payment_method === "stripe" ? "Carte" : "Virement"}</p>
            </div>
          </div>
          <p className="text-xs text-kay-ink/60 border-t border-kay-line pt-3">
            {o.items.map((i) => `${i.qty}× ${i.name}${i.variant ? ` (${i.variant})` : ""} (${i.brand})`).join(" · ")}
          </p>
          <div className="flex flex-wrap gap-2 items-center text-[10px] font-mono font-bold">
            {o.savings > 0 && <span className="text-kay-green">Cliente : -{fmt(o.savings)}</span>}
            {o.promo_code && <span className="text-kay-pink">Code {o.promo_code} : -{fmt(o.promo_discount)}</span>}
            {o.points_earned > 0 && <span className="text-kay-pink">+{o.points_earned} pts</span>}
          </div>
          <div className="flex flex-wrap gap-3 items-center border-t border-kay-line pt-3">
            <select value={o.status} onChange={(e) => patch(o, { status: e.target.value })} className="rounded-full border border-kay-line bg-white px-4 py-1.5 text-xs font-bold" data-testid={`order-status-select-${o.id}`}>
              {Object.entries(ORDER_STATUS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
            <button
              onClick={() => patch(o, { paid: !o.paid }, o.paid ? "Repassée en non payée" : "Paiement marqué comme reçu")}
              className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${o.paid ? "bg-kay-green text-white" : "border border-kay-line hover:border-kay-green"}`}
              data-testid={`order-paid-toggle-${o.id}`}
            >
              {o.paid ? "✓ Payée" : "Marquer payée"}
            </button>
            <TrackingInput order={o} onSaved={load} />
            <button
              onClick={async () => {
                if (!window.confirm(`Supprimer définitivement la commande ${o.number} ?`)) return;
                await api.delete(`/admin/orders/${o.id}`);
                toast.success("Commande supprimée");
                load();
              }}
              className="rounded-full border border-kay-line px-4 py-1.5 text-xs font-bold text-red-400 hover:border-red-300 hover:text-red-500 transition-colors flex items-center gap-1.5"
              data-testid={`order-delete-${o.id}`}
            >
              <Trash2 size={12} /> Supprimer
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

const EMPTY_PROMO = { code: "", type: "percent", value: "", min_order: "", start_date: "", end_date: "", max_uses: "", max_per_customer: "", product_ids: [], categories: [] };

function PromosTab() {
  const [promos, setPromos] = useState([]);
  const [products, setProducts] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => {
    api.get("/admin/promos").then((r) => setPromos(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data.filter((p) => !p.coming_soon))).catch(() => {});
    api.get("/admin/settings").then((r) => setScopes([...(r.data.categories || []), ...(r.data.subcategories || [])])).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const toggleIn = (key, v) => setForm({ ...form, [key]: form[key].includes(v) ? form[key].filter((x) => x !== v) : [...form[key], v] });

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/promos", {
        ...form,
        value: parseFloat(form.value),
        min_order: form.min_order === "" ? 0 : parseFloat(form.min_order),
        max_uses: form.max_uses === "" ? null : parseInt(form.max_uses),
        max_per_customer: form.max_per_customer === "" ? null : parseInt(form.max_per_customer),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      });
      toast.success(`Code ${form.code.toUpperCase()} enregistré`);
      setForm(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const inp = "rounded-xl border border-kay-line px-3.5 py-2.5 text-sm outline-none focus:border-kay-pink w-full";
  const chip = (on) =>
    `rounded-full px-3 py-1 text-[11px] font-semibold border transition-all ${on ? "bg-kay-ink text-white border-kay-ink" : "border-kay-line hover:border-kay-ink"}`;
  const describe = (p) =>
    [
      p.type === "fixed" ? `-${fmt(p.value)}` : `-${p.value} %`,
      p.min_order > 0 ? `dès ${fmt(p.min_order)} d'achat` : null,
      p.end_date ? `expire le ${new Date(p.end_date).toLocaleDateString("fr-FR")}` : null,
      p.max_uses ? `max ${p.max_uses} utilisations` : null,
      p.max_per_customer ? `max ${p.max_per_customer}× par cliente` : null,
      (p.product_ids?.length || p.categories?.length) ? "panier ciblé" : null,
    ].filter(Boolean).join(" · ");

  return (
    <div className="grid gap-4" data-testid="admin-promos-tab">
      <button onClick={() => setForm({ ...EMPTY_PROMO })} className="justify-self-start rounded-full bg-kay-ink text-white px-6 py-2.5 text-xs font-bold flex items-center gap-2 hover:bg-kay-pink transition-colors" data-testid="promo-new-button">
        <Plus size={14} /> Nouveau code promo
      </button>

      {form && (
        <form onSubmit={save} className="rounded-2xl border border-kay-rosedeep bg-kay-rose/20 p-5 grid gap-4" data-testid="promo-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <input required placeholder="CODE *" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={`${inp} font-mono uppercase`} data-testid="promo-code-input" />
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inp} data-testid="promo-type-select">
              <option value="percent">Pourcentage (%)</option>
              <option value="fixed">Montant fixe (€)</option>
            </select>
            <input required type="number" step="0.01" min="0" placeholder={form.type === "fixed" ? "Montant (€)" : "Pourcentage (%)"} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={inp} data-testid="promo-value-input" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="grid gap-1"><span className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/50">Montant min. de commande (€)</span>
              <input type="number" step="0.01" min="0" placeholder="0 = aucun" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} className={inp} data-testid="promo-min-order" /></label>
            <label className="grid gap-1"><span className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/50">Début (facultatif)</span>
              <input type="datetime-local" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inp} data-testid="promo-start" /></label>
            <label className="grid gap-1"><span className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/50">Expiration (facultatif)</span>
              <input type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inp} data-testid="promo-end" /></label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="grid gap-1"><span className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/50">Utilisations max. (total)</span>
              <input type="number" min="1" placeholder="Illimité" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className={inp} data-testid="promo-max-uses" /></label>
            <label className="grid gap-1"><span className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/50">Utilisations max. par cliente</span>
              <input type="number" min="1" placeholder="Illimité" value={form.max_per_customer} onChange={(e) => setForm({ ...form, max_per_customer: e.target.value })} className={inp} data-testid="promo-max-per-customer" /></label>
          </div>
          <div className="grid gap-2">
            <p className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/50">Catégories concernées (aucune = tout le site)</p>
            <div className="flex flex-wrap gap-2">
              {scopes.map((c) => (
                <button type="button" key={c.slug} onClick={() => toggleIn("categories", c.slug)} className={chip(form.categories.includes(c.slug))} data-testid={`promo-cat-${c.slug}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-2">
            <p className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/50">Produits concernés (aucun = tout le site)</p>
            <div className="flex flex-wrap gap-2">
              {products.map((p) => (
                <button type="button" key={p.id} onClick={() => toggleIn("product_ids", p.id)} className={chip(form.product_ids.includes(p.id))} data-testid={`promo-product-${p.id}`}>
                  {p.name}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="rounded-full bg-kay-ink text-white px-6 py-2.5 text-xs font-bold hover:bg-kay-pink transition-colors" data-testid="promo-save">Enregistrer le code</button>
            <button type="button" onClick={() => setForm(null)} className="rounded-full border border-kay-line px-6 py-2.5 text-xs font-bold" data-testid="promo-cancel">Annuler</button>
          </div>
        </form>
      )}

      {promos.length === 0 && !form && <p className="font-display italic text-2xl text-kay-ink/40 text-center py-8">Aucun code promo pour le moment.</p>}
      {promos.map((p) => (
        <div key={p.code} className="rounded-2xl border border-kay-line bg-white p-5 grid gap-2" data-testid={`promo-${p.code}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-lg font-bold">{p.code}</span>
            <span className="font-mono text-xs font-bold rounded-full bg-kay-rose px-3 py-1 text-kay-wine">{p.type === "fixed" ? `-${fmt(p.value)}` : `-${p.value} %`}</span>
            <span className={`font-mono text-[10px] uppercase tracking-widest ${p.active ? "text-kay-green" : "text-kay-ink/40"}`}>{p.active ? "Actif" : "Désactivé"}</span>
            <span className="font-mono text-[10px] text-kay-ink/40">utilisé {p.uses || 0}×</span>
            <div className="ml-auto flex gap-2">
              <button onClick={() => setForm({ ...EMPTY_PROMO, ...p, min_order: p.min_order || "", max_uses: p.max_uses ?? "", max_per_customer: p.max_per_customer ?? "", start_date: (p.start_date || "").slice(0, 16), end_date: (p.end_date || "").slice(0, 16), product_ids: p.product_ids || [], categories: p.categories || [] })} className="rounded-full border border-kay-line px-4 py-1.5 text-xs font-bold hover:border-kay-ink" data-testid={`promo-edit-${p.code}`}>
                <Pencil size={12} />
              </button>
              <button onClick={async () => { await api.patch(`/admin/promos/${p.code}`); load(); }} className="rounded-full border border-kay-line px-4 py-1.5 text-xs font-bold hover:border-kay-ink" data-testid={`promo-toggle-${p.code}`}>
                {p.active ? "Désactiver" : "Activer"}
              </button>
              <button onClick={async () => { if (window.confirm(`Supprimer le code ${p.code} ?`)) { await api.delete(`/admin/promos/${p.code}`); load(); } }} className="rounded-full border border-kay-line px-4 py-1.5 text-xs font-bold text-red-500 hover:border-red-400" data-testid={`promo-delete-${p.code}`}>
                <Trash2 size={12} />
              </button>
            </div>
          </div>
          <p className="text-xs text-kay-ink/50">{describe(p)}</p>
        </div>
      ))}
    </div>
  );
}

function IdeasTab() {
  const [ideas, setIdeas] = useState([]);
  const [grouped, setGrouped] = useState([]);
  const load = () => {
    api.get("/admin/ideas").then((r) => setIdeas(r.data)).catch(() => {});
    api.get("/admin/ideas/grouped").then((r) => setGrouped(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);
  const setStatus = async (i, status) => {
    await api.patch(`/admin/ideas/${i.id}`, { status });
    load();
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="admin-ideas-tab">
      <div className="rounded-2xl border border-kay-line bg-white p-5 h-fit" data-testid="ideas-ranking">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50 mb-4">Les plus demandés</p>
        {grouped.length === 0 && <p className="text-sm text-kay-ink/40">Aucune idée reçue.</p>}
        <div className="grid gap-3">
          {grouped.map((g, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`idea-group-${i}`}>
              <span className="font-display text-2xl text-kay-pink w-8">{i + 1}.</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{g.product_name}</p>
                <p className="text-[11px] text-kay-ink/40">{g.brand || "Marque libre"} · {g.categories.join(", ")}</p>
              </div>
              <span className="font-mono text-xs font-bold rounded-full bg-kay-rose px-2.5 py-1" data-testid={`idea-group-count-${i}`}>×{g.count}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="lg:col-span-2 grid gap-4 content-start">
        {ideas.map((i) => (
          <div key={i.id} className="rounded-2xl border border-kay-line bg-white p-5" data-testid={`admin-idea-${i.id}`}>
            <div className="flex flex-wrap justify-between gap-3">
              <div>
                <p className="font-display text-xl">{i.product_name} {i.brand && <span className="text-kay-pink">· {i.brand}</span>}</p>
                <p className="text-xs text-kay-ink/50 mt-0.5">{i.category} · par {i.pseudo || "Anonyme"} · {new Date(i.created_at).toLocaleDateString("fr-FR")}</p>
                {i.message && <p className="text-sm text-kay-ink/60 mt-2 italic">« {i.message} »</p>}
              </div>
              <select value={i.status} onChange={(e) => setStatus(i, e.target.value)} className="rounded-full border border-kay-line bg-white px-4 py-1.5 text-xs font-bold h-fit" data-testid={`idea-status-${i.id}`}>
                {Object.entries(IDEA_STATUS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertesTab() {
  const [waitlist, setWaitlist] = useState([]);
  const [contacts, setContacts] = useState([]);
  const load = () => {
    api.get("/admin/waitlist").then((r) => setWaitlist(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);
  const del = async (w) => {
    await api.delete(`/admin/waitlist/${w.id}`);
    toast.success("Alerte supprimée");
    load();
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start" data-testid="admin-alertes-tab">
      <div className="rounded-2xl border border-kay-line bg-white p-6" data-testid="waitlist-panel">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50 mb-1">Alertes « Me prévenir »</p>
        <p className="text-[11px] text-kay-ink/40 mb-4">Email de confirmation envoyé à l'inscription, email automatique au retour en stock. La cliente peut se désinscrire depuis l'email.</p>
        {waitlist.length === 0 && <p className="text-sm text-kay-ink/40">Aucune alerte pour le moment.</p>}
        <div className="grid gap-2.5">
          {waitlist.map((w) => (
            <div key={w.id} className="flex items-center gap-3 rounded-xl border border-kay-line px-4 py-3" data-testid={`waitlist-entry-${w.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{w.product_name}</p>
                <p className="text-[11px] text-kay-ink/50 truncate">{w.email} · {new Date(w.created_at).toLocaleDateString("fr-FR")}</p>
              </div>
              <span className={`font-mono text-[10px] font-bold px-2.5 py-1 rounded-full ${w.active !== false ? "bg-kay-green/10 text-kay-green" : "bg-kay-ink/5 text-kay-ink/40"}`} data-testid={`waitlist-active-${w.id}`}>
                {w.active !== false ? "ACTIVE" : "DÉSINSCRITE"}
              </span>
              <button onClick={() => del(w)} className="p-2 text-red-400 hover:text-red-600" aria-label="Supprimer l'alerte" data-testid={`waitlist-delete-${w.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-kay-line bg-white p-6" data-testid="contacts-panel">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50 mb-4">Messages de contact</p>
        {contacts.length === 0 && <p className="text-sm text-kay-ink/40">Aucun message reçu.</p>}
        <div className="grid gap-2.5">
          {contacts.map((c) => (
            <div key={c.id} className="rounded-xl border border-kay-line px-4 py-3" data-testid={`contact-entry-${c.id}`}>
              <p className="text-sm font-semibold">{c.name} <span className="font-normal text-kay-ink/50">· {c.email}</span></p>
              <p className="text-[11px] text-kay-ink/40">{new Date(c.created_at).toLocaleDateString("fr-FR")}</p>
              <p className="text-sm text-kay-ink/70 mt-1.5 italic">« {c.message} »</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TaxoImage({ value, onDone, testid }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/taxonomy-image", fd);
      onDone(data.path);
      toast.success("Image enregistrée");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} title="Changer l'image" data-testid={testid} className="w-11 h-11 rounded-lg border border-kay-line overflow-hidden bg-kay-rose/30 flex items-center justify-center shrink-0 hover:border-kay-pink transition-colors">
        {busy ? <Loader2 size={14} className="animate-spin text-kay-pink" /> : value ? <img src={fileUrl(value)} alt="" className="w-full h-full object-cover" /> : <ImagePlus size={14} className="text-kay-pink/60" />}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} />
    </>
  );
}

function CategoriesTab() {
  const [s, setS] = useState(null);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [newSub, setNewSub] = useState({ label: "", category: "maquillage" });
  const [newBrand, setNewBrand] = useState("");
  const load = () => {
    api.get("/admin/settings").then((r) => setS({ ...r.data, brands: r.data.brands || [], categories: r.data.categories || [], subcategories: r.data.subcategories || [] })).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);
  if (!s) return null;
  const inp = "rounded-xl border border-kay-line px-4 py-2.5 text-sm outline-none focus:border-kay-pink w-full";

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { categories: s.categories, subcategories: s.subcategories, brands: s.brands, all_image: s.all_image || "" });
      toast.success("Organisation enregistrée — la boutique est à jour");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const move = (key, i, dir) => {
    const arr = [...s[key]];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    arr.forEach((x, k) => (x.order = k));
    setS({ ...s, [key]: arr });
  };
  const upd = (key, i, patch) => setS({ ...s, [key]: s[key].map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const del = (key, i) => setS({ ...s, [key]: s[key].filter((_, j) => j !== i) });
  const toggleInList = (list, slug) => (list.includes(slug) ? list.filter((x) => x !== slug) : [...list, slug]);

  const upDown = (key, i) => (
    <span className="flex flex-col shrink-0">
      <button type="button" onClick={() => move(key, i, -1)} className="text-kay-ink/40 hover:text-kay-pink" aria-label="Monter" data-testid={`${key}-up-${i}`}><ChevronUp size={14} /></button>
      <button type="button" onClick={() => move(key, i, 1)} className="text-kay-ink/40 hover:text-kay-pink" aria-label="Descendre" data-testid={`${key}-down-${i}`}><ChevronDown size={14} /></button>
    </span>
  );

  const visibleCats = s.categories.filter((c) => !c.hidden);

  return (
    <div className="grid gap-6 max-w-3xl" data-testid="admin-categories-tab">
      <p className="text-xs text-kay-ink/50 -mt-2">
        Organise ta boutique sans toucher au code : chaque modification est appliquée à la boutique publique dès que tu cliques sur « Enregistrer ».
      </p>

      {/* CATÉGORIES */}
      <div className="rounded-2xl border border-kay-line bg-white p-6 grid gap-3" data-testid="taxo-categories">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50">Catégories</p>
        <p className="text-[11px] text-kay-ink/40">Chaque catégorie visible devient une page dédiée (/boutique/son-nom) avec sa grande carte sur la boutique.</p>
        <div className="flex items-center gap-2.5 rounded-xl border border-kay-rosedeep/60 bg-kay-rose/20 px-3 py-2.5" data-testid="taxo-all-image-row">
          <TaxoImage value={s.all_image || ""} onDone={(path) => setS({ ...s, all_image: path })} testid="taxo-all-image" />
          <div>
            <p className="text-sm font-semibold">Toutes les pépites</p>
            <p className="text-[11px] text-kay-ink/40">Image de la carte « Toutes les pépites » — pense à cliquer sur « Enregistrer l'organisation » en bas.</p>
          </div>
        </div>
        {s.categories.map((c, i) => {
          const count = products.filter((p) => p.category === c.slug).length;
          return (
            <div key={c.slug + i} className={`flex items-center gap-2.5 flex-wrap rounded-xl border px-3 py-2.5 ${c.hidden ? "border-dashed border-kay-ink/30 opacity-60" : "border-kay-line"}`} data-testid={`taxo-cat-${c.slug}`}>
              <TaxoImage value={c.image || ""} onDone={(path) => upd("categories", i, { image: path })} testid={`taxo-cat-image-${c.slug}`} />
              <input value={c.label} onChange={(e) => upd("categories", i, { label: e.target.value })} className={`${inp} !w-44`} data-testid={`taxo-cat-label-${c.slug}`} />
              <span className="font-mono text-[10px] text-kay-ink/40">{c.slug} · {count} produit{count > 1 ? "s" : ""}</span>
              <span className="flex-1" />
              {upDown("categories", i)}
              <button type="button" onClick={() => upd("categories", i, { hidden: !c.hidden })} title={c.hidden ? "Réafficher" : "Masquer temporairement"} className={`p-1.5 ${c.hidden ? "text-kay-pink" : "text-kay-ink/40 hover:text-kay-ink"}`} data-testid={`taxo-cat-hide-${c.slug}`}>
                {c.hidden ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (count > 0) return toast.error(`Déplace ou supprime d'abord les ${count} produit(s) de « ${c.label} »`);
                  if (window.confirm(`Supprimer la catégorie « ${c.label} » ?`)) del("categories", i);
                }}
                className="p-1.5 text-red-400 hover:text-red-600"
                aria-label="Supprimer"
                data-testid={`taxo-cat-delete-${c.slug}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}
        <div className="flex gap-2">
          <input placeholder="Nouvelle catégorie…" value={newCat} onChange={(e) => setNewCat(e.target.value)} className={inp} data-testid="taxo-cat-new" />
          <button
            type="button"
            onClick={() => {
              const label = newCat.trim();
              if (!label) return;
              const slug = slugify(label);
              if (s.categories.some((c) => c.slug === slug)) return toast.error("Cette catégorie existe déjà");
              setS({ ...s, categories: [...s.categories, { slug, label, hidden: false, image: "", order: s.categories.length }] });
              setNewCat("");
            }}
            className="rounded-full border border-kay-line px-5 text-xs font-bold whitespace-nowrap hover:border-kay-ink"
            data-testid="taxo-cat-add"
          >
            + Ajouter
          </button>
        </div>
      </div>

      {/* SOUS-CATÉGORIES */}
      <div className="rounded-2xl border border-kay-line bg-white p-6 grid gap-3" data-testid="taxo-subcategories">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50">Sous-catégories</p>
        <p className="text-[11px] text-kay-ink/40">Rattache chaque sous-catégorie à sa catégorie parente — tu peux la déplacer quand tu veux.</p>
        {s.subcategories.map((sc, i) => (
          <div key={sc.slug + i} className={`flex items-center gap-2.5 flex-wrap rounded-xl border px-3 py-2.5 ${sc.hidden ? "border-dashed border-kay-ink/30 opacity-60" : "border-kay-line"}`} data-testid={`taxo-sub-${sc.slug}`}>
            <input value={sc.label} onChange={(e) => upd("subcategories", i, { label: e.target.value })} className={`${inp} !w-44`} data-testid={`taxo-sub-label-${sc.slug}`} />
            <select value={sc.category || "maquillage"} onChange={(e) => upd("subcategories", i, { category: e.target.value })} className={`${inp} !w-40`} data-testid={`taxo-sub-parent-${sc.slug}`}>
              {s.categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.label}</option>
              ))}
            </select>
            <span className="flex-1" />
            {upDown("subcategories", i)}
            <button type="button" onClick={() => upd("subcategories", i, { hidden: !sc.hidden })} title={sc.hidden ? "Réafficher" : "Masquer"} className={`p-1.5 ${sc.hidden ? "text-kay-pink" : "text-kay-ink/40 hover:text-kay-ink"}`} data-testid={`taxo-sub-hide-${sc.slug}`}>
              {sc.hidden ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
            <button type="button" onClick={() => window.confirm(`Supprimer « ${sc.label} » ?`) && del("subcategories", i)} className="p-1.5 text-red-400 hover:text-red-600" aria-label="Supprimer" data-testid={`taxo-sub-delete-${sc.slug}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <div className="flex gap-2 flex-wrap">
          <input placeholder="Nouvelle sous-catégorie…" value={newSub.label} onChange={(e) => setNewSub({ ...newSub, label: e.target.value })} className={`${inp} flex-1 min-w-[180px]`} data-testid="taxo-sub-new" />
          <select value={newSub.category} onChange={(e) => setNewSub({ ...newSub, category: e.target.value })} className={`${inp} !w-40`} data-testid="taxo-sub-new-parent">
            {s.categories.map((c) => (
              <option key={c.slug} value={c.slug}>{c.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              const label = newSub.label.trim();
              if (!label) return;
              const slug = slugify(label);
              if (s.subcategories.some((x) => x.slug === slug)) return toast.error("Cette sous-catégorie existe déjà");
              setS({ ...s, subcategories: [...s.subcategories, { slug, label, category: newSub.category, hidden: false, order: s.subcategories.length }] });
              setNewSub({ label: "", category: newSub.category });
            }}
            className="rounded-full border border-kay-line px-5 text-xs font-bold whitespace-nowrap hover:border-kay-ink"
            data-testid="taxo-sub-add"
          >
            + Ajouter
          </button>
        </div>
      </div>

      {/* MARQUES */}
      <div className="rounded-2xl border border-kay-line bg-white p-6 grid gap-3" data-testid="taxo-brands">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50">Marques</p>
        <p className="text-[11px] text-kay-ink/40">
          Une marque n'apparaît dans une catégorie que si tu l'y associes ici. « Disponible » : visible quand elle a des produits dans la catégorie. « Coming soon » : tuile spéciale vers la page Coming soon. « Masquée » : invisible partout.
        </p>
        {s.brands.map((b, i) => {
          const subChoices = s.subcategories.filter((sc) => (b.categories || []).includes(sc.category));
          return (
            <div key={b.name + i} className={`rounded-xl border px-4 py-3.5 grid gap-3 ${b.status === "hidden" ? "border-dashed border-kay-ink/30 opacity-60" : "border-kay-line"}`} data-testid={`taxo-brand-${slugify(b.name)}`}>
              <div className="flex items-center gap-2.5 flex-wrap">
                <TaxoImage value={b.image || ""} onDone={(path) => upd("brands", i, { image: path })} testid={`taxo-brand-image-${slugify(b.name)}`} />
                <input value={b.name} onChange={(e) => upd("brands", i, { name: e.target.value })} className={`${inp} !w-48`} data-testid={`taxo-brand-name-${slugify(b.name)}`} />
                <select value={b.status || "available"} onChange={(e) => upd("brands", i, { status: e.target.value })} className={`${inp} !w-40`} data-testid={`taxo-brand-status-${slugify(b.name)}`}>
                  <option value="available">Disponible</option>
                  <option value="coming_soon">Coming soon</option>
                  <option value="hidden">Masquée</option>
                </select>
                <span className="flex-1" />
                {upDown("brands", i)}
                <button type="button" onClick={() => window.confirm(`Supprimer la marque « ${b.name} » ?`) && del("brands", i)} className="p-1.5 text-red-400 hover:text-red-600" aria-label="Supprimer" data-testid={`taxo-brand-delete-${slugify(b.name)}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/40">Apparaît dans :</span>
                {s.categories.map((c) => (
                  <label key={c.slug} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold cursor-pointer transition-colors ${(b.categories || []).includes(c.slug) ? "bg-kay-ink text-white border-kay-ink" : "border-kay-line hover:border-kay-pink"}`} data-testid={`taxo-brand-${slugify(b.name)}-cat-${c.slug}`}>
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={(b.categories || []).includes(c.slug)}
                      onChange={() => upd("brands", i, { categories: toggleInList(b.categories || [], c.slug) })}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
              {subChoices.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/40">Sous-catégories :</span>
                  {subChoices.map((sc) => (
                    <label key={sc.slug} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold cursor-pointer transition-colors ${(b.subcategories || []).includes(sc.slug) ? "bg-kay-pink text-white border-kay-pink" : "border-kay-line hover:border-kay-pink"}`} data-testid={`taxo-brand-${slugify(b.name)}-sub-${sc.slug}`}>
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={(b.subcategories || []).includes(sc.slug)}
                        onChange={() => upd("brands", i, { subcategories: toggleInList(b.subcategories || [], sc.slug) })}
                      />
                      {sc.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <div className="flex gap-2">
          <input placeholder="Nouvelle marque…" value={newBrand} onChange={(e) => setNewBrand(e.target.value)} className={inp} data-testid="taxo-brand-new" />
          <button
            type="button"
            onClick={() => {
              const name = newBrand.trim();
              if (!name) return;
              if (s.brands.some((b) => b.name.toLowerCase() === name.toLowerCase())) return toast.error("Cette marque existe déjà");
              setS({ ...s, brands: [...s.brands, { name, image: "", categories: [], subcategories: [], status: "available", order: s.brands.length }] });
              setNewBrand("");
            }}
            className="rounded-full border border-kay-line px-5 text-xs font-bold whitespace-nowrap hover:border-kay-ink"
            data-testid="taxo-brand-add"
          >
            + Ajouter
          </button>
        </div>
      </div>

      <button onClick={save} disabled={saving} className="justify-self-start rounded-full bg-kay-ink text-white px-8 py-3 text-xs font-bold hover:bg-kay-pink transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="taxo-save">
        {saving && <Loader2 size={13} className="animate-spin" />} Enregistrer l'organisation
      </button>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [heroBusy, setHeroBusy] = useState(false);
  const heroRef = useRef(null);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS({ ...r.data, brands: r.data.brands || [], categories: r.data.categories || [], subcategories: r.data.subcategories || [] })).catch(() => {});
  }, []);
  if (!s) return null;
  const uploadHero = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setHeroBusy(true);
    try {
      const { data } = await api.post("/admin/hero-image", fd);
      setS({ ...s, hero_image: data.path });
      toast.success("Bannière d'accueil mise à jour");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setHeroBusy(false);
    }
  };
  const removeHero = async () => {
    await api.delete("/admin/hero-image");
    setS({ ...s, hero_image: "" });
    toast.success("Bannière retirée — visuel par défaut restauré");
  };
  const save = async (e) => {
    e.preventDefault();
    await api.put("/admin/settings", {
      ...s,
      shipping_fee: parseFloat(s.shipping_fee),
      free_shipping_above: parseFloat(s.free_shipping_above),
      points_per_euro: parseFloat(s.points_per_euro),
      points_block: parseInt(s.points_block),
      block_value: parseFloat(s.block_value),
    });
    toast.success("Réglages enregistrés");
  };
  const inp = "rounded-xl border border-kay-line px-4 py-2.5 text-sm outline-none focus:border-kay-pink w-full";
  const m = s.maintenance || { enabled: false, message: "" };
  const toggleMaintenance = async () => {
    if (!m.enabled && !window.confirm("Activer le mode maintenance ? Les clientes ne pourront plus passer de commande.")) return;
    const nm = { ...m, enabled: !m.enabled, since: !m.enabled ? new Date().toISOString() : m.since };
    await api.put("/admin/settings", { maintenance: nm });
    setS({ ...s, maintenance: nm });
    toast.success(nm.enabled ? "Mode maintenance activé — commandes bloquées" : "Mode maintenance désactivé — boutique ouverte");
  };
  const field = (label, key, type = "text") => (
    <label className="grid gap-1.5">
      <span className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/50">{label}</span>
      <input type={type} step="0.01" value={s[key]} onChange={(e) => setS({ ...s, [key]: e.target.value })} className={inp} data-testid={`setting-${key}`} />
    </label>
  );
  return (
    <form onSubmit={save} className="grid gap-4 max-w-3xl" data-testid="admin-settings-tab">
      <div className="rounded-2xl border border-kay-line bg-white p-6 grid gap-3" data-testid="hero-image-section">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50">Bannière d'accueil</p>
        <p className="text-[11px] text-kay-ink/40">Grande photo d'ambiance en haut de ta page d'accueil. Sans photo, l'illustration par défaut est utilisée. Effet immédiat, pas besoin d'enregistrer.</p>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-24 h-32 rounded-xl border border-kay-line overflow-hidden bg-kay-rose/30 flex items-center justify-center">
            {s.hero_image ? (
              <img src={fileUrl(s.hero_image)} alt="Bannière actuelle" className="w-full h-full object-cover" data-testid="hero-image-preview" />
            ) : (
              <ImagePlus size={18} className="text-kay-pink/50" />
            )}
          </div>
          <div className="grid gap-2">
            <button type="button" onClick={() => heroRef.current?.click()} disabled={heroBusy} className="rounded-full bg-kay-ink text-white px-5 py-2.5 text-xs font-bold hover:bg-kay-pink transition-colors flex items-center gap-2" data-testid="hero-image-upload">
              {heroBusy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
              {s.hero_image ? "Remplacer la bannière" : "Ajouter une bannière"}
            </button>
            {s.hero_image && (
              <button type="button" onClick={removeHero} className="rounded-full border border-kay-line px-5 py-2.5 text-xs font-bold hover:border-red-300 hover:text-red-500 transition-colors" data-testid="hero-image-remove">
                Retirer (visuel par défaut)
              </button>
            )}
          </div>
        </div>
        <input ref={heroRef} type="file" accept="image/*" className="hidden" onChange={uploadHero} data-testid="hero-image-input" />
      </div>

      <div className="rounded-2xl border border-kay-line bg-white p-6 grid gap-4">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50">Paiement & contact</p>
        {field("Adresse PayPal (affichée après commande)", "paypal_email")}
        {field("IBAN pour les virements", "iban")}
        {field("Snapchat", "snapchat")}
        <label className="grid gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/50">Date du prochain drop (compte à rebours Coming soon)</span>
          <input type="datetime-local" value={(s.drop_date || "").slice(0, 16)} onChange={(e) => setS({ ...s, drop_date: e.target.value })} className={inp} data-testid="setting-drop-date" />
        </label>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50 pt-2">Livraison</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {field("Frais de livraison (€)", "shipping_fee", "number")}
          {field("Livraison offerte dès (€)", "free_shipping_above", "number")}
        </div>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50 pt-2">Programme fidélité</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {field("Points gagnés par €", "points_per_euro", "number")}
          {field("Bloc de points", "points_block", "number")}
          {field("Valeur du bloc (€)", "block_value", "number")}
        </div>
      </div>

      <div className="rounded-2xl border border-kay-line bg-white p-6 grid gap-4" data-testid="payment-methods-section">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50">Moyens de paiement</p>
        <p className="text-[11px] text-kay-ink/40">Active ou désactive les moyens de paiement proposés au checkout. Désactiver ne supprime jamais la configuration (clés, identifiants) : tout reste stocké en sécurité côté serveur. Il doit toujours rester au moins un moyen actif.</p>
        {[
          ["paypal", "PayPal", "Paiement en ligne sécurisé, commande marquée payée automatiquement"],
          ["virement", "Virement bancaire", "La cliente reçoit les instructions après validation"],
        ].map(([k, label, hint]) => {
          const pm = s.payment_methods || { paypal: true, virement: true };
          const on = pm[k] !== false;
          return (
            <div key={k} className={`flex items-center gap-4 rounded-xl border px-4 py-3.5 transition-colors ${on ? "border-kay-line" : "border-dashed border-kay-ink/25 opacity-70"}`} data-testid={`pm-row-${k}`}>
              <div className="flex-1">
                <p className="text-sm font-bold">{label}</p>
                <p className="text-[11px] text-kay-ink/40">{hint}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                data-testid={`pm-toggle-${k}`}
                onClick={() => {
                  const next = { ...pm, [k]: !on };
                  if (!Object.values(next).some(Boolean)) return toast.error("Il doit rester au moins un moyen de paiement actif");
                  setS({ ...s, payment_methods: next });
                }}
                className={`relative w-12 h-7 rounded-full transition-colors duration-300 shrink-0 ${on ? "bg-kay-green" : "bg-kay-ink/20"}`}
              >
                <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all duration-300 ${on ? "left-6" : "left-1"}`} />
              </button>
              <span className={`font-mono text-[10px] font-bold w-16 text-right ${on ? "text-kay-green" : "text-kay-ink/40"}`} data-testid={`pm-status-${k}`}>{on ? "ACTIVÉ" : "DÉSACTIVÉ"}</span>
            </div>
          );
        })}
        <p className="text-[11px] text-kay-ink/40">Clique sur « Enregistrer » en bas pour appliquer.</p>
      </div>

      <div className="rounded-2xl border border-kay-line bg-white p-6 grid gap-4" data-testid="notifications-section">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50">Notifications par email</p>
        {field("Adresse email qui reçoit les notifications de la boutique", "admin_email", "email")}
        <div className="grid sm:grid-cols-2 gap-2.5">
          {Object.entries(NOTIFY_LABELS).map(([k, label]) => (
            <label key={k} className="flex items-center gap-2.5 text-xs font-semibold rounded-xl border border-kay-line px-4 py-3 cursor-pointer hover:border-kay-pink transition-colors" data-testid={`notif-label-${k}`}>
              <input
                type="checkbox"
                checked={!!(s.notify_prefs || {})[k]}
                onChange={(e) => setS({ ...s, notify_prefs: { ...(s.notify_prefs || {}), [k]: e.target.checked } })}
                className="accent-[#E05297]"
                data-testid={`notif-${k}`}
              />
              {label}
            </label>
          ))}
        </div>
        <p className="text-[11px] text-kay-ink/40">Pense à cliquer sur « Enregistrer » en bas pour appliquer tes choix.</p>
      </div>

      <div className="rounded-2xl border border-kay-line bg-white p-6 grid gap-4" data-testid="policies-section">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50">Fiches produits — Livraison, retours, authenticité</p>
        {[
          ["shipping", "Livraison"],
          ["returns", "Retours"],
          ["authenticity", "Authenticité"],
        ].map(([k, label]) => (
          <label key={k} className="grid gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/50">{label}</span>
            <textarea rows={2} value={(s.policies || {})[k] || ""} onChange={(e) => setS({ ...s, policies: { ...(s.policies || {}), [k]: e.target.value } })} className={`${inp} resize-none`} data-testid={`policy-${k}`} />
          </label>
        ))}
      </div>

      <div className={`rounded-2xl border p-6 grid gap-3 ${m.enabled ? "border-kay-pink bg-kay-rose/30" : "border-kay-line bg-white"}`} data-testid="maintenance-section">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-kay-ink/50">Mode maintenance</p>
        {m.enabled && m.since && (
          <p className="text-xs text-kay-pink font-semibold" data-testid="maintenance-since">
            Activé le {new Date(m.since).toLocaleString("fr-FR")} — les commandes sont bloquées, ton admin reste accessible.
          </p>
        )}
        <label className="grid gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-widest text-kay-ink/50">Message affiché aux clientes</span>
          <textarea rows={2} value={m.message} onChange={(e) => setS({ ...s, maintenance: { ...m, message: e.target.value } })} className={`${inp} resize-none`} data-testid="maintenance-message-input" />
        </label>
        <button
          type="button"
          onClick={toggleMaintenance}
          className={`justify-self-start rounded-full px-8 py-3 text-xs font-bold transition-colors ${m.enabled ? "bg-kay-green text-white hover:bg-kay-ink" : "bg-kay-ink text-white hover:bg-kay-pink"}`}
          data-testid="maintenance-toggle"
        >
          {m.enabled ? "Désactiver le mode maintenance" : "Activer le mode maintenance"}
        </button>
      </div>

      <button className="justify-self-start rounded-full bg-kay-ink text-white px-8 py-3 text-xs font-bold hover:bg-kay-pink transition-colors" data-testid="settings-save">Enregistrer</button>
    </form>
  );
}

export default function Admin() {
  const { user, isAdmin, logout } = useAuth();
  const [tab, setTab] = useState("produits");

  if (user === undefined) return <div className="py-32 text-center font-display italic text-2xl text-kay-ink/40">Chargement…</div>;
  if (!isAdmin) return <Login />;

  const TABS = [
    { v: "produits", label: "Produits" },
    { v: "categories", label: "Catégories & Marques" },
    { v: "commandes", label: "Commandes" },
    { v: "promos", label: "Codes promo" },
    { v: "idees", label: "Idées clientes" },
    { v: "alertes", label: "Alertes" },
    { v: "reglages", label: "Réglages" },
  ];

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-14 pb-10" data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-3">Espace privé</p>
          <h1 className="font-display text-4xl sm:text-5xl">Tableau de <span className="italic text-kay-pink">bord</span></h1>
        </div>
        <button onClick={logout} className="flex items-center gap-2 rounded-full border border-kay-line px-5 py-2.5 text-xs font-bold hover:border-kay-ink transition-colors" data-testid="admin-logout">
          <LogOut size={14} /> Déconnexion
        </button>
      </div>
      <div className="flex gap-2 flex-wrap mb-8">
        {TABS.map((t) => (
          <button
            key={t.v}
            data-testid={`admin-tab-${t.v}`}
            onClick={() => setTab(t.v)}
            className={`rounded-full px-5 py-2 text-xs font-bold transition-all ${tab === t.v ? "bg-kay-ink text-white" : "border border-kay-line hover:border-kay-ink"}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "produits" && <ProductsTab />}
      {tab === "categories" && <CategoriesTab />}
      {tab === "commandes" && <OrdersTab />}
      {tab === "promos" && <PromosTab />}
      {tab === "idees" && <IdeasTab />}
      {tab === "alertes" && <AlertesTab />}
      {tab === "reglages" && <SettingsTab />}
    </div>
  );
}
